import { useCallback, useEffect } from 'react';
import { RegisterOptions } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert, Button, Flex, If, Select, Text } from '@/lib/v2/components';

import { useRulesEmailSender } from './useRulesEmailSender.hooks';

import { TRANSLATE_CREATE_EMAIL_ACTION } from '@/modules/CampaignsModule/constants';
import { IFormProps } from '@/modules/CampaignsModule/interfaces/CampaignActions';
import { IRulesEmailFormInfo } from '@/modules/RulesModule/interfaces';

interface ISenderInputProps {
  loading?: boolean;
  form: IFormProps<IRulesEmailFormInfo>;
  rules: Record<string, RegisterOptions>;
  disabled?: boolean;
}
const SenderInput = ({ form, loading, rules, disabled = false }: ISenderInputProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { control } = form;

  const {
    events: { getSenders, handleChangeSender },
    state: { senderDropdownOptions, loading: senderLoading, isInvalidSender },
  } = useRulesEmailSender({ setValue: form.setValue });

  useEffect(() => void getSenders(), [getSenders]);

  const handleNavigate = useCallback(() => {
    navigate('/v2/configuration/senders');
  }, [navigate]);

  return (
    <>
      <Text
        className="sticky top-0 border-b border-solid border-emblue-borders bg-emblue-white pb-2"
        color="primary"
        fontWeight="medium"
        variant="sub-headline"
      >
        {t(`${TRANSLATE_CREATE_EMAIL_ACTION}.sender`)}
      </Text>
      <Flex column withGap className="relative mt-9 pl-px" itemAlignment="start">
        <Select
          control={control}
          disabled={disabled}
          id="create-action-sender-select"
          isLoading={loading || senderLoading}
          name="sender"
          options={senderDropdownOptions}
          rules={rules?.sender}
          onChange={handleChangeSender}
        />
        <If condition={isInvalidSender}>
          <Alert
            className="mb-1"
            description={t(`${TRANSLATE_CREATE_EMAIL_ACTION}.senderAlert`)}
            type="warning"
          />
        </If>
        <Button
          gray
          outline
          disabled={disabled}
          id="create-sender"
          size="small"
          onClick={handleNavigate}
        >
          {t(`${TRANSLATE_CREATE_EMAIL_ACTION}.createSender`)}
        </Button>
      </Flex>
    </>
  );
};

export default SenderInput;
